import React from 'react';
import { Box, Stack, Paper, Typography, Divider, Button } from '@mui/material';
import { X, Lock, Clock } from 'lucide-react';

interface SystemSetupCardProps {
  assessmentTitle?: string;
  totalPoints?: number;
  durationMinutes?: number;
}

export const SystemSetupCard: React.FC<SystemSetupCardProps> = ({
  assessmentTitle = 'HiAcuity Assessment',
  totalPoints = 2,
  durationMinutes = 60,
}) => {
  const pad = (n: number) => String(n).padStart(2, '0');
  const hours = Math.floor(durationMinutes / 60);
  const mins = durationMinutes % 60;
  const durationLabel = `${pad(hours)}:${pad(mins)} minutes`;

  return (
    <Paper
      variant='outlined'
      sx={{
        width: '100%',
        borderRadius: 3,
        overflow: 'hidden',
        textAlign: 'left',
      }}
    >
      {/* Modal-style header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2.5,
          py: 1.5,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'action.hover',
        }}
      >
        <Typography variant='subtitle2' fontWeight={600} color='text.primary'>
          {assessmentTitle}
        </Typography>
        <Box
          sx={{
            width: 26,
            height: 26,
            borderRadius: '50%',
            bgcolor: 'error.50',
            color: 'error.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <X size={14} />
        </Box>
      </Box>

      {/* Body */}
      <Stack spacing={2} sx={{ p: 3 }}>
        <Typography variant='subtitle2' fontWeight={600} color='text.primary'>
          {assessmentTitle}
        </Typography>

        {/* Points & Duration row */}
        <Stack direction='row' spacing={3} alignItems='center'>
          <Stack direction='row' spacing={1} alignItems='center'>
            <Lock size={16} color='#1976d2' />
            <Typography variant='caption' fontWeight={600} color='primary.main'>
              Total: {totalPoints} {totalPoints === 1 ? 'point' : 'points'}
            </Typography>
          </Stack>

          <Stack direction='row' spacing={1} alignItems='center'>
            <Clock size={16} color='#757575' />
            <Typography variant='caption' color='text.secondary'>
              Duration: {durationLabel}
            </Typography>
          </Stack>
        </Stack>

        <Divider />

        {/* Disclaimer */}
        <Typography variant='caption' color='text.secondary' sx={{ lineHeight: 1.6 }}>
          Disclaimer: During the process of the assessment your browser actions will be monitored.
        </Typography>

        {/* Warning */}
        <Typography variant='caption' color='error.main' fontWeight={500} sx={{ lineHeight: 1.6 }}>
          Warning: Once you start the assessment, you must complete it and you cannot re-take it again.
        </Typography>

        {/* Start button */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 0.5 }}>
          <Button
            variant='contained'
            color='success'
            size='small'
            sx={{ textTransform: 'none', fontWeight: 600, fontSize: 12 }}
          >
            Start Assessment
          </Button>
        </Box>
      </Stack>
    </Paper>
  );
};
